<template>
  <q-page class="q-pa-md">
    <div class="row items-center q-mb-md">
      <div class="text-h6">IAM — Users</div>
      <q-space />
      <q-select
        dense
        outlined
        v-model="departmentFilter"
        :options="departmentOptions"
        label="Department"
        clearable
        class="q-mr-sm"
        style="width: 100%; max-width: 220px"
      />
      <q-select
        dense
        outlined
        v-model="activeFilter"
        :options="activeFilterOptions"
        label="Status"
        emit-value
        map-options
        class="q-mr-sm"
        style="width: 100%; max-width: 180px"
      />
      <q-input
        dense
        outlined
        v-model="filter"
        placeholder="Search by username / email / employee name / ID"
        class="q-mr-sm"
        style="width: 100%; max-width: 300px"
        clearable
      />
      <q-btn
        color="primary"
        icon="add"
        label="Create User"
        @click="openCreate"
      />
    </div>

    <q-table
      :rows="filteredRows"
      :columns="columns"
      row-key="id"
      :loading="loading"
      flat
      bordered
      class="user-table"
    >
      <template v-slot:body-cell-employee="props">
        <q-td>
          <div v-if="props.row.employee">
            <div class="text-weight-medium">{{ props.row.employee.name }}</div>
            <div class="text-caption">
              {{ props.row.employee.employee_id }} •
              {{ props.row.employee.department }}
            </div>
          </div>
          <span v-else class="text-grey-6">—</span>
        </q-td>
      </template>

      <template v-slot:body-cell-department="props">
        <q-td>
          <span v-if="props.row.employee?.department">
            {{ props.row.employee.department }}
          </span>
          <span v-else class="text-grey-6">—</span>
        </q-td>
      </template>

      <template v-slot:body-cell-date_joined="props">
        <q-td>
          <span v-if="props.row.date_joined">
            {{ formatDateTime(props.row.date_joined) }}
          </span>
          <span v-else class="text-grey-6">—</span>
        </q-td>
      </template>

      <template v-slot:body-cell-last_login="props">
        <q-td>
          <span v-if="props.row.last_login">
            {{ formatDateTime(props.row.last_login) }}
          </span>
          <span v-else class="text-grey-6">—</span>
        </q-td>
      </template>

      <template v-slot:body-cell-is_active="props">
        <q-td>
          {{ props.row.is_active ? 'Yes' : 'No' }}
        </q-td>
      </template>

      <template v-slot:body-cell-groups="props">
        <q-td>
          <q-chip
            v-for="g in props.row.groups || []"
            :key="g"
            dense
            class="q-mr-xs"
          >
            {{ g }}
          </q-chip>
          <span v-if="!(props.row.groups || []).length" class="text-grey-6"
            >—</span
          >
        </q-td>
      </template>

      <template v-slot:body-cell-actions="props">
        <q-td class="text-right">
          <q-btn dense flat icon="edit" @click="openEdit(props.row)" />
          <q-btn
            dense
            flat
            icon="delete"
            color="negative"
            @click="confirmDelete(props.row)"
          />
        </q-td>
      </template>
    </q-table>

    <!-- Create/Edit dialog -->
    <q-dialog v-model="dialog" persistent>
      <q-card style="min-width: 320px; max-width: 95vw; width: 800px">
        <q-card-section class="row items-center">
          <div class="text-h6">{{ form.id ? 'Edit User' : 'Create User' }}</div>
          <q-space />
          <q-btn dense flat icon="close" v-close-popup />
        </q-card-section>

        <q-separator />

        <q-card-section class="q-pa-md">
          <q-form class="q-gutter-md" @submit.prevent="save">
            <div class="row q-col-gutter-md">
              <!-- User -->
              <div class="col-12 col-md-6">
                <div class="text-subtitle2 q-mb-sm">Login</div>

                <q-input
                  outlined
                  v-model="form.username"
                  label="Username"
                  autocomplete="off"
                  :rules="[(v) => !!v || 'Username is required']"
                />

                <q-input
                  outlined
                  v-model="form.email"
                  label="Email"
                  autocomplete="off"
                />

                <q-select
                outlined
                v-model="form.employee.gender"
                :options="genderOptions"
                label="Gender"
                emit-value
                map-options
                clearable
                />

                <q-toggle v-model="form.is_active" label="Active" />

                <q-select
                  outlined
                  v-model="form.groups"
                  :options="groupOptions"
                  label="Groups"
                  multiple
                  use-chips
                  emit-value
                  map-options
                />
              </div>

              <!-- Employee -->
              <div class="col-12 col-md-6">
                <div class="row items-center q-mb-sm">
                  <div class="text-subtitle2">Employee</div>
                  <q-space />
                  <q-toggle v-model="attachEmployee" label="Attach" />
                </div>

                <div v-if="attachEmployee" class="q-gutter-sm">
                  <q-input
                    outlined
                    v-model="form.employee.employee_id"
                    label="Employee ID"
                    readonly
                    hint="Auto-generated unique ID"
                    :loading="generatingId"
                  />
                  <q-input
                    outlined
                    v-model="form.employee.name"
                    label="Employee Name"
                    :rules="[(v) => !!v || 'Employee name required']"
                  />

                  <q-select
                    outlined
                    v-model="form.employee.organization"
                    :options="organizationOptions"
                    label="Company/Organization"
                    option-value="value"
                    option-label="label"
                    emit-value
                    map-options
                    :loading="loadingOrganizations"
                    hint="Select which organization this employee belongs to"
                  />

                  <q-input
                    outlined
                    v-model="form.employee.date_of_birth"
                    type="date"
                    label="Date of Birth"
                  />

                  <q-select
                    outlined
                    v-model="form.employee.department"
                    :options="departmentOptions"
                    label="Department"
                    emit-value
                    map-options
                  />

                  <q-select
                    outlined
                    v-model="form.employee.position"
                    :options="positionOptions"
                    label="Position"
                    emit-value
                    map-options
                  />

                  <q-input
                    outlined
                    v-model="form.employee.start_date"
                    type="date"
                    label="Start Date"
                  />
                </div>

                <div v-else class="text-grey-6">
                  Employee record will be <b>empty</b> (employee: null).
                </div>
              </div>
            </div>

            <q-input
              v-if="attachEmployee"
              outlined
              v-model="form.employee.job_description"
              type="textarea"
              label="Job Description"
              autogrow
              @update:model-value="(v) => (form.employee.job_description = formatJobDescription(v))"
              hint="Use one bullet per line"
            />

            <div class="row q-gutter-sm">
              <q-btn
                type="submit"
                color="primary"
                label="Save"
                :loading="saving"
              />

              <q-btn
                v-if="form.id"
                color="secondary"
                label="Resend Invite"
                @click="resendInviteLink"
                :loading="resendingInvite"
              />

              <q-btn flat label="Cancel" v-close-popup />
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Delete confirm -->
    <q-dialog v-model="deleteDialog">
      <q-card style="min-width: 420px">
        <q-card-section class="text-h6">Delete user?</q-card-section>
        <q-card-section>
          This will delete <b>{{ toDelete?.username }}</b
          >.
        </q-card-section>
        <q-card-actions align="right">
          <q-btn flat label="Cancel" v-close-popup />
          <q-btn
            color="negative"
            label="Delete"
            @click="remove"
            :loading="deleting"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-page>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { api } from '@/api';
import { useQuasar } from 'quasar';

const genderOptions = ref([
  { label: 'Male (Herr)', value: 'male' },
  { label: 'Female (Frau)', value: 'female' },
  { label: 'Non-binary', value: 'non_binary' },
  { label: 'Prefer not to say', value: 'unspecified' },
]);

const $q = useQuasar();

const loading = ref(false);
const saving = ref(false);
const deleting = ref(false);

const resendingInvite = ref(false);

function formatDateTime(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function handleError(error, defaultMessage = 'An unexpected error occurred') {
  console.error('Error:', error);

  let message = defaultMessage;

  if (error?.response?.data) {
    if (typeof error.response.data === 'object') {
      const errors = error.response.data;
      const errorMessages = [];

      Object.keys(errors).forEach((key) => {
        if (Array.isArray(errors[key])) {
          errorMessages.push(`${key}: ${errors[key].join(', ')}`);
        } else if (typeof errors[key] === 'object') {
          Object.keys(errors[key]).forEach((nestedKey) => {
            if (Array.isArray(errors[key][nestedKey])) {
              errorMessages.push(
                `${key}.${nestedKey}: ${errors[key][nestedKey].join(', ')}`,
              );
            }
          });
        } else {
          errorMessages.push(`${key}: ${errors[key]}`);
        }
      });

      if (errorMessages.length > 0) {
        message = errorMessages.join('; ');
      }
    } else if (typeof error.response.data === 'string') {
      message = error.response.data;
    }
  } else if (error?.message) {
    message = error.message;
  }

  $q.notify({
    type: 'negative',
    message: message,
    position: 'top-right',
    timeout: 5000,
    actions: [{ icon: 'close', color: 'white', handler: () => {} }],
  });

  return message;
}

function formatJobDescription(value) {
  if (!value) return '• ';
  const lines = value.split('\n').map((line) => {
    const trimmed = line.trimStart();
    return trimmed.startsWith('• ')
      ? line
      : `• ${trimmed.replace(/^•\s*/, '')}`;
  });
  return lines.join('\n');
}

const rows = ref([]);
const filter = ref('');
const departmentFilter = ref(null);
const activeFilter = ref('all');

const dialog = ref(false);
const deleteDialog = ref(false);
const toDelete = ref(null);

const groupOptions = ref([]);
const attachEmployee = ref(false);

const generatingId = ref(false);

const organizationOptions = ref([]);
const loadingOrganizations = ref(false);

const departmentOptions = ref([
  'Engineering',
  'Marketing',
  'Sales',
  'Human Resources',
  'Finance',
  'Operations',
  'Customer Support',
  'Product Management',
  'Design',
  'Quality Assurance',
  'Legal',
  'Administration',
]);

const activeFilterOptions = [
  { label: 'All', value: 'all' },
  { label: 'Yes', value: 'active' },
  { label: 'No', value: 'inactive' },
];

const positionOptions = ref([
  'Software Engineer',
  'Senior Software Engineer',
  'Lead Software Engineer',
  'Engineering Manager',
  'Product Manager',
  'Senior Product Manager',
  'Marketing Manager',
  'Sales Representative',
  'Sales Manager',
  'HR Manager',
  'Financial Analyst',
  'Operations Manager',
  'Customer Support Specialist',
  'Designer',
  'QA Engineer',
  'Legal Counsel',
  'Office Manager',
  'Executive Assistant',
]);

const columns = [
  {
    name: 'username',
    label: 'Username',
    field: 'username',
    align: 'left',
    sortable: true,
  },
  {
    name: 'email',
    label: 'Email',
    field: 'email',
    align: 'left',
    sortable: true,
  },
  { name: 'employee', label: 'Employee', field: 'employee', align: 'left' },
  {
    name: 'department',
    label: 'Department',
    field: (row) => row.employee?.department || '',
    align: 'left',
    sortable: true,
  },
  {
    name: 'date_joined',
    label: 'Date Joined',
    field: 'date_joined',
    align: 'left',
    sortable: true,
  },
  {
    name: 'last_login',
    label: 'Last Login',
    field: 'last_login',
    align: 'left',
    sortable: true,
  },
  { name: 'groups', label: 'Groups', field: 'groups', align: 'left' },
  {
    name: 'is_active',
    label: 'Active',
    field: 'is_active',
    align: 'left',
    sortable: true,
    format: (v) => (v ? 'Yes' : 'No'),
  },
  { name: 'actions', label: 'Actions', field: 'actions', align: 'right' },
];

const blankEmployee = () => ({
  employee_id: '',
  name: '',
  gender: '',
  position: '',
  start_date: '',
  date_of_birth: '',
  department: '',
  job_description: '• ',
  organization: null,
});

const blankForm = () => ({
  id: null,
  username: '',
  email: '',
  is_active: true,
  groups: [],
  employee: blankEmployee(),
});

const form = ref(blankForm());

const filteredRows = computed(() => {
  const q = (filter.value || '').toLowerCase().trim();
  const dept = (departmentFilter.value || '').toLowerCase().trim();
  const status = activeFilter.value || 'all';

  return rows.value.filter((u) => {
    const emp = u.employee || {};
    const matchesSearch =
      !q ||
      (u.username || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (emp.name || '').toLowerCase().includes(q) ||
      (emp.employee_id || '').toLowerCase().includes(q);

    const matchesDept =
      !dept || (emp.department || '').toLowerCase().includes(dept);

    const matchesStatus =
      status === 'all' ||
      (status === 'active' && u.is_active) ||
      (status === 'inactive' && !u.is_active);

    return matchesSearch && matchesDept && matchesStatus;
  });
});

async function loadUsers() {
  loading.value = true;

  try {
    const res = await api.get('iam/users/');
    rows.value = res.data || [];
  } catch (e) {
    handleError(
      e,
      'Failed to load users. Please check your connection and try again.',
    );
    rows.value = [];
  } finally {
    loading.value = false;
  }
}

async function loadGroups() {
  try {
    const res = await api.get('iam/groups/');
    groupOptions.value = (res.data || []).map((g) => ({
      label: g.name,
      value: g.name,
    }));
  } catch (e) {
    console.warn(
      'Could not load groups:',
      e?.response?.status,
      e?.response?.data,
    );
    handleError(e, 'Failed to load groups. Some functionality may be limited.');
    groupOptions.value = [];
  }
}

function openCreate() {
  form.value = {
    id: null,
    username: '',
    email: '',
    is_active: true,
    groups: [],
    employee: blankEmployee(),
  };
  attachEmployee.value = true;

  if (attachEmployee.value) {
    generateEmployeeId();
  }

  dialog.value = true;
}

function openEdit(row) {
  form.value = {
    id: row.id,
    username: row.username || '',
    email: row.email || '',
    is_active: row.is_active ?? true,
    groups: row.groups || [],
    employee: row.employee
      ? {
          ...row.employee,
          gender: row.employee.gender || '',
          job_description: formatJobDescription(row.employee.job_description || ''),
          organization: row.employee.organization?.id || null,
        }
      : blankEmployee(),
  };
  attachEmployee.value = !!row.employee;
  dialog.value = true;
}

async function generateEmployeeId() {
  generatingId.value = true;

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    const res = await api.get('iam/users/generate_employee_id/', {
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    form.value.employee.employee_id = res.data.employee_id;
  } catch (e) {
    if (e.name === 'AbortError') {
      handleError(
        e,
        'Employee ID generation timed out. Using fallback method.',
      );
    } else {
      console.error('Failed to generate employee ID:', e);
      handleError(e, 'Failed to generate employee ID. Using fallback method.');
    }

    try {
      form.value.employee.employee_id = `EMP${Date.now().toString().slice(-6)}`;
    } catch (fallbackError) {
      console.error('Fallback ID generation also failed:', fallbackError);
      form.value.employee.employee_id = 'EMP000001';
    }
  } finally {
    generatingId.value = false;
  }
}

watch(attachEmployee, (newVal) => {
  if (newVal && !form.value.employee.employee_id) {
    generateEmployeeId();
  }
});

async function save() {
  saving.value = true;
  try {
    const payload = JSON.parse(JSON.stringify(form.value));

    if (!attachEmployee.value) {
      payload.employee = null;
    } else {
      if (!payload.employee?.organization) {
        $q.notify({
          type: 'negative',
          message: 'Organization is required for employees',
          position: 'top-right',
        });
        saving.value = false;
        return;
      }
      if (payload.id && payload.employee) {
        delete payload.employee.employee_id;
      }
    }

    if (payload.id) {
      await api.put(`iam/users/${payload.id}/`, payload);
    } else {
      await api.post('iam/users/', payload);
    }

    dialog.value = false;
    await loadUsers();
  } catch (e) {
    handleError(
      e,
      'Failed to save user. Please check your data and try again.',
    );
  } finally {
    saving.value = false;
  }
}

function confirmDelete(row) {
  toDelete.value = row;
  deleteDialog.value = true;
}

async function remove() {
  if (!toDelete.value) return;
  deleting.value = true;

  try {
    await api.delete(`iam/users/${toDelete.value.id}/`);
    deleteDialog.value = false;
    toDelete.value = null;
    await loadUsers();
  } catch (e) {
    handleError(e, 'Failed to delete user.');
  } finally {
    deleting.value = false;
  }
}

async function resendInviteLink() {
  if (!form.value.id) return;

  resendingInvite.value = true;
  try {
    const res = await api.post(`users/${form.value.id}/resend-invite/`);
    $q.notify({
      type: 'positive',
      message: res.data.detail,
      position: 'top-right',
      timeout: 3000,
    });
  } catch (e) {
    handleError(e, 'Failed to resend invite');
  } finally {
    resendingInvite.value = false;
  }
}

async function loadOrganizations() {
  loadingOrganizations.value = true;
  try {
    const res = await api.get('organizations/');
    organizationOptions.value = (res.data || []).map((org) => ({
      label: `${org.name}${org.domain ? ' (' + org.domain + ')' : ''}`,
      value: org.id,
    }));
  } catch (e) {
    console.warn('Could not load organizations:', e);
    organizationOptions.value = [];
  } finally {
    loadingOrganizations.value = false;
  }
}

onMounted(async () => {
  try {
    await Promise.allSettled([loadUsers(), loadGroups(), loadOrganizations()]);
  } catch (e) {
    console.error('Page initialization error:', e);
    handleError(e, 'Failed to initialize page. Please refresh.');
  }
});
</script>

<style scoped>
/* Mobile responsiveness */
@media (max-width: 768px) {
  .user-table {
    font-size: 0.875rem;
  }

  .user-table .q-table__top {
    flex-direction: column;
    align-items: stretch;
  }

  .user-table .q-table__top .q-field {
    margin-bottom: 8px;
  }
}

/* Dialog responsiveness */
@media (max-width: 600px) {
  .q-dialog .q-card {
    margin: 8px !important;
    max-height: 90vh;
  }

  .q-dialog .q-card-section {
    padding: 16px !important;
  }
}

/* Form responsiveness */
@media (max-width: 600px) {
  .q-form .row .col-12.col-md-6 {
    padding: 8px;
  }

  .q-form .q-gutter-md > * {
    margin-bottom: 16px !important;
  }
}
</style>
